import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, filter, of, tap } from 'rxjs';
import { ReportService } from '../services/report.service';
import { DownloadLegacyReport, DownloadReport, LoadReports, RequestReport } from './report.action';
import { ReportOperations } from '../operations/report.operations';

interface ReportStateModel {
    reports: string[];
    loading: boolean;
    requesting: boolean;
    downloading: boolean;
    downloadingLegacy: boolean;
}

@State<ReportStateModel>({
    name: 'report',
    defaults: {
        reports: [],
        loading: true,
        requesting: false,
        downloading: false,
        downloadingLegacy: false
    }
})
@Injectable()
export class ReportState {
    constructor(private reportService: ReportService) {}

    @Selector()
    static reports(state: ReportStateModel): string[] {
        return state?.reports;
    }

    @Selector()
    static requesting(state: ReportStateModel): boolean {
        return state?.requesting;
    }

    @Selector()
    static downloading(state: ReportStateModel): boolean {
        return state?.downloading;
    }

    @Selector()
    static downloadingLegacy(state: ReportStateModel): boolean {
        return state?.downloadingLegacy;
    }

    @Action(LoadReports)
    loadReports(ctx: StateContext<ReportStateModel>) {
        ctx.patchState({ loading: true });

        return this.reportService.getReports().pipe(
            catchError(() => {
                ctx.patchState({ loading: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    reports: response
                        .filter(report => !report.Key?.startsWith('legacy/'))
                        .map(report => report.Key ?? 'report.xlsx'),
                    loading: false
                })
            )
        );
    }

    @Action(RequestReport)
    requestReport(ctx: StateContext<ReportStateModel>, { email }: RequestReport) {
        ctx.patchState({ requesting: true });

        return this.reportService.requestReport(email).pipe(
            tap(() => ctx.patchState({ requesting: false })),
            catchError(() => {
                ctx.patchState({ requesting: false });
                return of(void 0);
            })
        );
    }

    @Action(DownloadReport)
    downloadReport(ctx: StateContext<ReportStateModel>, { reportName }: DownloadReport) {
        ctx.patchState({ downloading: true });

        return this.reportService.getReportDownloadLink(reportName).pipe(
            catchError(() => {
                ctx.patchState({ downloading: false });
                return of(void 0);
            }),
            tap(url => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ReportOperations.downloadFile(url!, reportName);
                ctx.patchState({ downloading: false });
            })
        );
    }

    @Action(DownloadLegacyReport)
    downloadLegacyReport(ctx: StateContext<ReportStateModel>) {
        ctx.patchState({ downloadingLegacy: true });

        return this.reportService.getLegacyReportDownloadLink().pipe(
            catchError(() => {
                ctx.patchState({ downloadingLegacy: false });
                return of(void 0);
            }),
            tap(url => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ReportOperations.downloadFile(url!, 'legacy-report.xlsx');
                ctx.patchState({ downloadingLegacy: false });
            })
        );
    }
}
